import { useEffect, useState, useMemo } from 'react';
import { Search, Filter, ArrowUpRight, Users, Inbox, MailCheck, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';

const STATUS_OPTIONS = [
    { value: 'all', label: 'All statuses' },
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'archived', label: 'Archived' },
];

export default function AdminLeadsPage() {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, qualified: 0 });
    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const params = {};
            if (statusFilter !== 'all') params.status = statusFilter;
            if (q) params.q = q;
            const [a, b] = await Promise.all([
                api.get('/admin/leads', { params }),
                api.get('/admin/leads/stats'),
            ]);
            setLeads(a.data.items || []);
            setStats(b.data || {});
        } catch (err) {
            toast.error('Failed to load leads');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const t = setTimeout(() => load(), 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, statusFilter]);

    const setLeadStatus = async (id, status) => {
        try {
            await api.patch(`/admin/leads/${id}`, { status });
            toast.success(`Marked as ${status}`);
            load();
            setOpen((o) => (o ? { ...o, status } : null));
        } catch { toast.error('Update failed'); }
    };

    const cards = useMemo(() => ([
        { label: 'Total', value: stats.total || 0, icon: Users },
        { label: 'New', value: stats.new || 0, icon: Inbox },
        { label: 'Contacted', value: stats.contacted || 0, icon: MailCheck },
        { label: 'Qualified', value: stats.qualified || 0, icon: CheckCircle2 },
    ]), [stats]);

    return (
        <div data-testid="admin-leads-page" className="space-y-6">
            <header>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Leads</h1>
                <p className="text-sm text-muted-foreground mt-1">All Book a Consultation submissions land here. Triage and update status.</p>
            </header>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {cards.map((c) => (
                    <div key={c.label} className="dt-glass rounded-xl p-4 sm:p-5">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{c.label}</span>
                            <c.icon className="h-4 w-4 text-primary" />
                        </div>
                        <div className="mt-2 text-2xl sm:text-3xl font-semibold font-display">{c.value}</div>
                    </div>
                ))}
            </div>

            <div className="dt-glass rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, company…" className="pl-9 bg-white/5 border-white/10 focus-visible:ring-primary" data-testid="admin-leads-search-input" />
                </div>
                <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[170px] bg-white/5 border-white/10" data-testid="admin-leads-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
            </div>

            <div className="dt-glass rounded-2xl overflow-hidden">
                <Table data-testid="admin-leads-table">
                    <TableHeader>
                        <TableRow className="border-white/10 hover:bg-transparent">
                            <TableHead className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Name</TableHead>
                            <TableHead className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Email</TableHead>
                            <TableHead className="text-xs font-mono uppercase tracking-widest text-muted-foreground hidden md:table-cell">Company</TableHead>
                            <TableHead className="text-xs font-mono uppercase tracking-widest text-muted-foreground hidden lg:table-cell">Project</TableHead>
                            <TableHead className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Status</TableHead>
                            <TableHead className="text-xs font-mono uppercase tracking-widest text-muted-foreground hidden sm:table-cell">Created</TableHead>
                            <TableHead></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
                        ) : leads.length === 0 ? (
                            <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">No leads yet.</TableCell></TableRow>
                        ) : (
                            leads.map((lead) => (
                                <TableRow key={lead.id} className="border-white/10 hover:bg-white/5 cursor-pointer" data-testid="admin-leads-table-row" onClick={() => setOpen(lead)}>
                                    <TableCell className="font-medium">{lead.name}</TableCell>
                                    <TableCell className="text-foreground/80">{lead.email}</TableCell>
                                    <TableCell className="hidden md:table-cell text-foreground/80">{lead.company || '—'}</TableCell>
                                    <TableCell className="hidden lg:table-cell text-foreground/80">{lead.project_type || '—'}</TableCell>
                                    <TableCell><StatusBadge status={lead.status} /></TableCell>
                                    <TableCell className="hidden sm:table-cell text-xs font-mono text-muted-foreground">{new Date(lead.created_at).toLocaleString()}</TableCell>
                                    <TableCell><ArrowUpRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
                <DialogContent className="max-w-2xl bg-[rgba(8,12,20,0.95)] border-white/10 text-foreground" data-testid="admin-lead-detail-dialog">
                    {open && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-xl">{open.name}</DialogTitle>
                                <div className="text-sm text-muted-foreground">{open.email} • {open.company || '—'}</div>
                            </DialogHeader>
                            <div className="grid grid-cols-2 gap-3 mt-2 text-sm">
                                <Info label="Role" value={open.role || '—'} />
                                <Info label="Project type" value={open.project_type || '—'} />
                                <Info label="Budget" value={open.budget || '—'} />
                                <Info label="Status" value={open.status} />
                                <Info label="Source" value={open.source} />
                                <Info label="Created" value={new Date(open.created_at).toLocaleString()} />
                            </div>
                            <div>
                                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-2">Message</div>
                                <div className="mt-2 rounded-lg border border-white/10 bg-white/5 p-3 text-sm whitespace-pre-wrap leading-relaxed">{open.message}</div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {['new', 'contacted', 'qualified', 'archived'].map((s) => (
                                    <Button key={s} variant="secondary" size="sm" onClick={() => setLeadStatus(open.id, s)} data-testid={`admin-lead-set-${s}`} className={`border ${open.status === s ? 'border-primary/60 text-primary' : 'border-white/10 text-foreground/85'}`}>
                                        Mark {s}
                                    </Button>
                                ))}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        new: 'border-primary/40 text-primary bg-primary/10',
        contacted: 'border-amber-400/40 text-amber-300 bg-amber-400/10',
        qualified: 'border-[hsl(152_95%_55%)]/40 text-[hsl(152_95%_70%)] bg-[hsl(152_95%_55%)]/10',
        archived: 'border-white/15 text-muted-foreground bg-white/5',
    };
    return <Badge variant="outline" className={`font-mono text-[10px] tracking-widest uppercase ${map[status] || map.new}`}>{status}</Badge>;
}
function Info({ label, value }) {
    return (
        <div className="rounded-lg border border-white/10 bg-white/5 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="mt-1 text-foreground/90 break-words">{value}</div>
        </div>
    );
}
